// Function index:
//   makeTokenContainer(kind, isSelf, name, foreign?) — build the shared
//       circle+label Pixi Container for a token (character or NPC), with no
//       layer/positioning opinions. Extracted so IsoRenderer can reuse the
//       exact same visuals as the flat renderer's token-registry.
//   makeToken(c, layer)             — build a Pixi character token + label.
//   makeNpcToken(n, layer)          — build a smaller, distinct NPC token.
//   placeTokenAt(handle, q, r, ...) — position a token on a hex, fanning
//       out when multiple tokens share the same cell so labels stay readable.

import { Container, Graphics, Sprite, Text, type Ticker } from 'pixi.js';
import { FOREIGN_TOKEN_COLOUR, HEX_SIZE, HexTile, NPC_TOKEN_COLOUR, OTHER_TOKEN_COLOUR, SELF_TOKEN_COLOUR } from './palette.js';
import { tweenPosition } from './anim.js';
import { mapStyle, getTokenTexture } from './atlas.js';
import type { CharacterInput, NpcInput, TokenHandle } from './types.js';

export function makeTokenContainer(
  kind: 'character' | 'npc',
  isSelf: boolean,
  name: string,
  foreign = false,
): { container: Container; graphics: Graphics; label: Text } {
  const container = new Container();
  const g = new Graphics();
  if (kind === 'npc') {
    g.circle(0, 0, HEX_SIZE * 0.30)
      .fill({ color: NPC_TOKEN_COLOUR })
      .stroke({ color: 0x000, width: 2 });
    container.addChild(g);
    maybeAddTokenSprite(container, 'token-npc', HEX_SIZE * 0.52);
    const label = new Text({
      text: name.split(' ')[0] ?? name,
      style: { fill: 0x000, fontSize: 9, fontFamily: 'system-ui', fontStyle: 'italic' },
    });
    label.anchor.set(0.5);
    container.addChild(label);
    return { container, graphics: g, label };
  }
  const fill = foreign ? FOREIGN_TOKEN_COLOUR : isSelf ? SELF_TOKEN_COLOUR : OTHER_TOKEN_COLOUR;
  g.circle(0, 0, HEX_SIZE * 0.42)
    .fill({ color: fill })
    .stroke({ color: 0x000, width: 2 });
  container.addChild(g);
  // Phase 5: optional portrait/top-down sprite over the affiliation circle.
  maybeAddTokenSprite(container, foreign ? 'token-other' : isSelf ? 'token-self' : 'token-other', HEX_SIZE * 0.7);
  const label = new Text({
    text: name.split(' ')[0] ?? name,
    style: { fill: 0x000, fontSize: 11, fontFamily: 'system-ui' },
  });
  label.anchor.set(0.5);
  container.addChild(label);
  return { container, graphics: g, label };
}

export function makeToken(c: CharacterInput, layer: Container): TokenHandle {
  const { container, graphics, label } = makeTokenContainer('character', c.isSelf, c.name, c.foreign);
  layer.addChild(container);
  return { container, graphics, label, isSelf: c.isSelf, kind: 'character' };
}

// Adds a token sprite over the circle when the map is showing art at all AND
// the token atlas provides the frame. The colored circle remains the affiliation
// ring; container.destroy({children:true}) cleans the sprite up on removal.
function maybeAddTokenSprite(container: Container, key: string, size: number): void {
  if (mapStyle() === 'plain') return;
  const tex = getTokenTexture(key);
  if (!tex) return;
  const sp = new Sprite(tex);
  sp.anchor.set(0.5);
  sp.width = size;
  sp.height = size;
  sp.eventMode = 'none';
  container.addChild(sp);
}

// NPCs render smaller and in a dimmer hue so players can read at a glance
// who's a teammate and who's an NPC at the same hex. The label is italicised.
export function makeNpcToken(n: NpcInput, layer: Container): TokenHandle {
  const { container, graphics, label } = makeTokenContainer('npc', false, n.name);
  layer.addChild(container);
  return { container, graphics, label, isSelf: false, kind: 'npc' };
}

export function placeTokenAt(
  handle: TokenHandle,
  q: number,
  r: number,
  all: Iterable<TokenHandle>,
  // When a ticker is supplied the token eases to its new cell instead of
  // snapping — used for moves. Initial placement omits it (instant).
  anim?: { ticker: Ticker; durationMs?: number },
): void {
  // HexTile is a defineHex factory; instances are cheap (a few numbers +
  // computed corners). We no longer need a Grid lookup table — Phase B3
  // streams hexes in/out, so the source of truth for "is this hex on the
  // map" lives in the scene's hexData map, not in a preloaded Grid.
  const hex = new HexTile({ q, r });
  const others = [...all].filter((t) => t !== handle &&
    Math.abs(t.container.x - hex.x) < 1 && Math.abs(t.container.y - hex.y) < 1);
  const offsetIndex = others.length;
  const offsets: Array<[number, number]> = [[0, 0], [10, -6], [-10, -6], [10, 6], [-10, 6]];
  const [dx, dy] = offsets[offsetIndex] ?? [0, 0];
  const toX = hex.x + dx;
  const toY = hex.y + dy;
  if (anim) tweenPosition(anim.ticker, handle.container, toX, toY, anim.durationMs);
  else { handle.container.x = toX; handle.container.y = toY; }
}
